import useSWR from "swr";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Leaf, Info, ArrowDown, ArrowUp, TrendingDown } from "lucide-react";
import { fetcher } from "@/lib/api";
import { Button } from "@/components/ui/button";
import TraceabilityDrawer from "./TraceabilityDrawer";

function KpiCard({ label, value, unit, tone = "ink", tooltip, testid, badge }) {
  const tones = {
    ink: "bg-white text-salnes-ink border-salnes-stone/20",
    emerald: "bg-salnes-emerald text-white border-salnes-emerald",
    atlantic: "bg-salnes-atlantic text-white border-salnes-atlantic",
    sand: "bg-salnes-sand/60 text-salnes-ink border-salnes-sand",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid={testid}
      title={tooltip}
      className={`rounded-2xl border p-6 min-h-[140px] flex flex-col justify-between ${tones[tone]}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">
          {label}
        </div>
        {badge}
      </div>
      <div>
        <div className="font-serif text-4xl leading-none tabular-nums">
          {value ?? "—"}
        </div>
        {unit && <div className="text-xs mt-1 opacity-70">{unit}</div>}
      </div>
    </motion.div>
  );
}

export default function PegadaResumo({ period }) {
  const { t } = useTranslation();
  const [tracOpen, setTracOpen] = useState(false);
  const { data: summary } = useSWR(`/carbon/summary?period=${period}`, fetcher);
  const { data: baseline } = useSWR("/carbon/baseline", fetcher);

  const scopeData = summary
    ? [{
        scope: t("fase3.scope1"),
        value: summary.breakdown_por_scope?.["1"] || 0,
      }, {
        scope: t("fase3.scope2"),
        value: summary.breakdown_por_scope?.["2"] || 0,
      }, {
        scope: t("fase3.scope3"),
        value: summary.breakdown_por_scope?.["3"] || 0,
      }]
    : [];

  const totalT = summary && !summary.empty_fleet ? (summary.total_kgco2e_t?.toFixed(2) || "0") : "—";
  const deltaKg = baseline?.delta_kgco2e ?? null;
  const deltaPct = baseline?.delta_pct ?? null;

  return (
    <div data-testid="pegada-resumo">
      {summary?.empty_fleet && (
        <div
          className="mb-4 rounded-xl border hairline bg-salnes-sand/40 text-salnes-ink text-sm px-4 py-3 flex items-center gap-2"
          data-testid="pegada-empty-notice"
          title={t("fase3.no_data_tooltip")}
        >
          <Info size={15} /> {t("fase3.no_fleet")}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-total-co2e"
          label={t("fase3.total_co2e")}
          value={totalT}
          unit={summary?.empty_fleet ? "" : "t CO₂e"}
          tone="atlantic"
          tooltip={summary?.empty_fleet ? t("fase3.no_data_tooltip") : undefined}
        />
        <KpiCard
          testid="kpi-intensity"
          label={t("fase3.intensity")}
          value={summary?.intensity_kgco2e_per_t ?? "—"}
          unit={summary?.intensity_kgco2e_per_t ? "kg CO₂e/t" : ""}
          tone="ink"
          tooltip={summary?.empty_fleet ? t("fase3.no_data_tooltip") : undefined}
        />
        <KpiCard
          testid="kpi-km-total"
          label={t("fase3.km_total")}
          value={summary && !summary.empty_fleet && summary.km_total ? Math.round(summary.km_total).toLocaleString("es-ES") : "—"}
          unit={summary && !summary.empty_fleet && summary.km_total ? "km" : ""}
          tone="sand"
          tooltip={summary?.empty_fleet ? t("fase3.no_data_tooltip") : undefined}
        />
        <KpiCard
          testid="kpi-vehicles-active"
          label={t("fase3.vehicles_active")}
          value={summary?.empty_fleet ? "—" : summary?.vehiculos_activos}
          tone="emerald"
          tooltip={summary?.empty_fleet ? t("fase3.no_data_tooltip") : undefined}
        />
      </div>

      {baseline && (
        <div
          className="mt-4 rounded-xl border hairline bg-white px-5 py-4 flex flex-wrap items-center gap-3 text-sm"
          data-testid="baseline-strip"
        >
          <TrendingDown size={16} className={deltaKg < 0 ? "text-salnes-emerald" : "text-salnes-coral"} />
          <span className="text-salnes-stone">
            {t("fase3.vs_baseline", { year: baseline.baseline_year })}:
          </span>
          <span className="font-serif text-lg text-salnes-ink tabular-nums">
            {deltaKg != null ? `${(deltaKg / 1000).toFixed(2)} t` : "—"}
          </span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
            deltaPct < 0 ? "bg-salnes-emerald/10 text-salnes-emerald" : "bg-salnes-coral/10 text-salnes-coral"
          }`}>
            {deltaPct < 0 ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
            {deltaPct != null ? `${Math.abs(deltaPct)}%` : "—"}
          </span>
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTracOpen(true)}
              data-testid="btn-traceability"
              className="rounded-full"
            >
              <Leaf size={13} className="mr-1.5" />
              {t("fase3.view_traceability")}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-2xl border hairline bg-white p-6" data-testid="chart-by-scope">
        <div className="text-[11px] uppercase tracking-[0.22em] text-salnes-stone mb-3">
          {t("fase3.total_co2e")} · {t("fase3.scope1")} / {t("fase3.scope2")} / {t("fase3.scope3")}
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={scopeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="scope" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="value" name="kg CO₂e" fill="#0B4C7A" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <TraceabilityDrawer
        open={tracOpen}
        onClose={() => setTracOpen(false)}
        data={{
          formula: "kgCO₂e = Σ (km × consumo_medio_l/km × factor_kgco2e/l)",
          inputs: {
            period: summary?.period,
            vehicles: summary?.vehiculos_activos,
            km_total: summary?.km_total,
            litros_estimados: summary?.litros_estimados,
            weighins_count: summary?.weighins_count,
          },
          factor: {
            category: "combustible",
            scope: 1,
            value_kgco2e_per_unit: 2.518,
            unit_input: "l",
            source_name: "MITECO 2024 — Factores de emisión",
            source_url: "https://www.miteco.gob.es/es/cambio-climatico/temas/mitigacion-politicas-y-medidas/factores_emision_tcm30-479095.pdf",
            version: 1,
            valid_from: "2024-01-01",
          },
          computed_at: new Date().toISOString(),
        }}
      />
    </div>
  );
}
